class SoundManager {
  private static instance: SoundManager;
  private audioContext: AudioContext | null = null;
  private sounds: Map<string, AudioBuffer> = new Map();
  private volume: number = 0.5;
  private enabled: boolean = true;

  // Sound file paths
  private readonly soundFiles = {
    notification: '/sounds/notification.wav'
  };

  private constructor() {
    this.initializeAudioContext();
  }

  static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  private initializeAudioContext(): void {
    try {
      // Create audio context on first user interaction to comply with browser policies
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass();
        this.preloadSounds();
      }
    } catch (error) {
      console.error('Failed to initialize audio context:', error);
    }
  }

  private async preloadSounds(): Promise<void> {
    if (!this.audioContext) return;

    for (const [key, path] of Object.entries(this.soundFiles)) {
      try {
        const response = await fetch(path);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        this.sounds.set(key, audioBuffer);
      } catch (error) {
        console.error(`Failed to preload sound ${key}:`, error);
      }
    }
  }

  async play(soundName: keyof typeof SoundManager.prototype.soundFiles): Promise<void> {
    if (!this.enabled || !this.audioContext) {
      return;
    }

    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        console.error('Failed to resume audio context:', error);
        return;
      }
    }

    const buffer = this.sounds.get(soundName);
    if (!buffer) {
      console.warn(`Sound ${soundName} not loaded`);
      return;
    }

    try {
      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();

      source.buffer = buffer;
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      gainNode.gain.value = this.volume;

      source.start(0);
    } catch (error) {
      console.error(`Failed to play sound ${soundName}:`, error);
    }
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume / 100));
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getVolume(): number {
    return Math.round(this.volume * 100);
  }

  // Convenience method for notification sound
  playNotification(): Promise<void> {
    return this.play('notification');
  }

  // Initialize on first user interaction
  async initialize(): Promise<void> {
    if (!this.audioContext) {
      this.initializeAudioContext();
    }

    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }
}

export default SoundManager.getInstance();